import { useState } from "react";
import { Button } from "./ui/button";
import { Check } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { API } from "../App";
import AvatarFigure from "./AvatarFigure";
import axios from "axios";

const COLOR_OPTIONS = [
  { id: "blue", value: "#002FA7", label: "Azul" },
  { id: "violet", value: "#5B21B6", label: "Violeta" },
  { id: "emerald", value: "#10B981", label: "Esmeralda" },
  { id: "amber", value: "#D97706", label: "Ambar" },
  { id: "rose", value: "#E11D48", label: "Rosa" },
  { id: "cyan", value: "#0891B2", label: "Cian" },
  { id: "indigo", value: "#4F46E5", label: "Indigo" },
  { id: "teal", value: "#0D9488", label: "Teal" },
];

const AvatarSetup = ({ onComplete }) => {
  const { t } = useLanguage();
  const [selectedColor, setSelectedColor] = useState("#002FA7");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewState, setPreviewState] = useState("clear");

  const handleSave = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/avatar/configure`, { color: selectedColor }, { withCredentials: true });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || "Error saving avatar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="avatar-setup-prompt">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in relative max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="font-['Outfit'] text-xl font-semibold text-[#111111] mb-1" data-testid="avatar-setup-title">
            {t("avatar_setup_title")}
          </h2>
          <p className="text-sm text-[#6B7280]">{t("avatar_setup_subtitle")}</p>
        </div>

        {/* Avatar Preview */}
        <div className="flex justify-center mb-6" data-testid="avatar-setup-preview">
          <div className="relative">
            <AvatarFigure state={previewState} color={selectedColor} size={160} interactive={false} />
          </div>
        </div>

        {/* State preview buttons */}
        <div className="flex justify-center gap-2 mb-6">
          {["dormant", "awakening", "clear", "radiant"].map((s) => (
            <button
              key={s}
              onClick={() => setPreviewState(s)}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                previewState === s
                  ? "bg-[#111111] text-white"
                  : "bg-[#F5F5F7] text-[#6B7280] hover:bg-[#E5E5EA]"
              }`}
              data-testid={`avatar-state-preview-${s}`}
            >
              {t(`avatar_state_${s}`)}
            </button>
          ))}
        </div>

        {/* Color Picker */}
        <div className="mb-6">
          {error && <p className="text-sm text-red-500 mb-3" data-testid="avatar-setup-error">{error}</p>}
          <p className="text-sm font-medium text-[#111111] mb-3">{t("avatar_choose_color")}</p>
          <div className="grid grid-cols-4 gap-3">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedColor(c.value)}
                className={`relative w-full aspect-square rounded-xl transition-all ${
                  selectedColor === c.value
                    ? "ring-2 ring-offset-2 ring-[#111111] scale-105"
                    : "hover:scale-105"
                }`}
                style={{ backgroundColor: c.value }}
                data-testid={`avatar-color-${c.id}`}
              >
                {selectedColor === c.value && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={loading}
          className="w-full h-12 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl font-medium"
          data-testid="avatar-setup-save"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            t("avatar_setup_save")
          )}
        </Button>
      </div>
    </div>
  );
};

export default AvatarSetup;
